import PropTypes from 'prop-types';
import { action, extendObservable } from 'mobx';
import debounce from 'lodash/debounce';
import { apiCall } from '../../API';
import reLooksLikeUrl from './../../urlValidator';
import AsyncActionStore from '../AsyncActionStore';
import FlagStore from '../FlagStore';
import FuseSelectStore from '../FuseSelectStore';

const DEFAULT_VALUES = {
    matchAllCategories: false,
    customerCategories: [],
    standardCategories: [],
};
const STANDARD_CATEGORIES = {
    HIGH_RISK_SITE: 1001,
    PSAT: 1002
};
export default class CategoriesSectionStore {
    static PropType = PropTypes.shape({
        originalValue: PropTypes.any,
        matchAllCategories: FlagStore.PropType.isRequired,
        customerCategoriesSelect: FuseSelectStore.PropType.isRequired,
        standardCategoriesSelect: FuseSelectStore.PropType.isRequired,
        apiCategories: PropTypes.func.isRequired,
        apiCustomCategoriesList: PropTypes.func,
        standardCategoriesLoadStatus: AsyncActionStore.PropType,
        customCategoriesLoadStatus: AsyncActionStore.PropType,
        searchStandardCategories: PropTypes.func.isRequired,
        apiSearchStandardCategories: PropTypes.func.isRequired,
        searchStandardCategoriesStatus: AsyncActionStore.PropType,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class CategoriesSectionStore */{
            originalValue: {},
            matchAllCategories: new FlagStore(),
            customerCategoriesSelect: new FuseSelectStore(),
            standardCategoriesSelect: new FuseSelectStore({
                sorterFactory: this.catSorterFactory
            }),
            apiCategories: apiCall('/console/browser_policy/list'),
            apiCustomCategoriesList: apiCall('/console/custom-categories/list'),
            loadCategoriesStatus: new AsyncActionStore(),
            loadCustomCategoriesStatus: new AsyncActionStore(),
            apiSearchStandardCategories: apiCall('/console/categories?includeHighRisk=true'),
            searchStandardCategoriesStatus: new AsyncActionStore(),
            externalValidationFn: null,
        }, data);
    }

    load = action(() => {
        if (!this.loadCategoriesStatus.isLoading) {
            this.loadCategoriesStatus.setLoading(this.apiCategories()
                .then(({ categories }) => categories)
                .then(this.syncSelect(this.standardCategoriesSelect))
            );
        }
        if (!this.loadCustomCategoriesStatus.isLoading) {
            this.loadCustomCategoriesStatus.setLoading(this.apiCustomCategoriesList()
                .then(this.syncSelect(this.customerCategoriesSelect))
            );
        }
        return Promise.all([
            this.loadCategoriesStatus.promise,
            this.loadCustomCategoriesStatus.promise,
        ]);
    });

    checkStandardCategoryFilter = () => {
        if (!reLooksLikeUrl.test(this.standardCategoriesSelect.filter.getValue() || '')) {
            this.searchStandardCategoriesStatus.setBase();
            this.standardCategoriesSelect.setFilterValues(null);
            return false;
        }
        return true;
    };

    debounceStandardCategoriesSearch = debounce(() => {
        if (this.checkStandardCategoryFilter()) {
            this.searchStandardCategoriesStatus.setLoading(
                this.apiSearchStandardCategories({ value: this.standardCategoriesSelect.filter.getValue() })
                    .then((cats) => cats.map((c) => c.id))
                    .then(this.standardCategoriesSelect.setFilterValues)
            );
        }
    }, 500); // 500ms used in the original implementation, 500ms used here

    searchStandardCategories = action((event) => {
        this.standardCategoriesSelect.filter.onChangeHandler(event);

        if (this.checkStandardCategoryFilter()) {
            this.debounceStandardCategoriesSearch();
        }
    });

    getExtraOptions = (item) => {
        // Hardcoded ids for High Risk sites and PSAT cats
        if ([STANDARD_CATEGORIES.PSAT, STANDARD_CATEGORIES.HIGH_RISK_SITE].includes(item.id)) {
            const extraOption = { icon: 'logo' };
            if (STANDARD_CATEGORIES.HIGH_RISK_SITE === item.id) {
                extraOption.info = 'URLs known to recently host malicious content based upon Proofpoint\'s ' +
                    'extensive threat intelligence data.';
            }
            return extraOption;
        }
        return {};
    }

    sortHash = (cat) =>
        `${cat.icon ? 'A|' : 'Z|'}${cat.label}`;

    sorter = (a, b) =>
        (this.sortHash(a) > this.sortHash(b) ? 1 : -1);

    catSorterFactory = (options, hash) => {
        const optsHash = hash || this.standardCategoriesSelect.hashByValue(options);

        return (v1, v2) => {
            const a = optsHash[v1];
            const b = optsHash[v2];
            if (!a) {
                return b;
            }
            if (!b) {
                return a;
            }
            return this.sorter(a, b);
        };
    }

    toSelectData = (item) => ({
        ...item,
        label: item.name,
        value: this.toSelectValue(item),
        ...this.getExtraOptions(item)
    })

    toSelectValue = (item) => (typeof item.id !== 'undefined' ? item.id : (item.name || item));

    syncSelect = (selectStore) => (arr) =>
        selectStore.setOptions(arr.map(this.toSelectData).sort(this.sorter));

    setSelectInitialState = (select, items) => {
        // do NOT overwrite categories if they have been loaded already
        if (!select.options.length) {
            this.syncSelect(select)(items);
        }
        select.setSelectedValues(items.map(this.toSelectValue));
    }

    setValue = action((value) => {
        value = { ...DEFAULT_VALUES, ...value };
        this.originalValue = value;

        this.matchAllCategories.setValue(value.matchAllCategories);
        this.setSelectInitialState(this.customerCategoriesSelect, value.customerCategories);
        this.setSelectInitialState(this.standardCategoriesSelect, value.standardCategories);
    });

    getValue = () => ({
        customerCategories: this.customerCategoriesSelect.selectedValues,
        standardCategories: this.standardCategoriesSelect.selectedValues,
        matchAllCategories: this.matchAllCategories.getValue(),
    });

    isValid = () => {
        const cur = this.getValue();
        if (this.externalValidationFn) {
            return this.externalValidationFn(cur);
        }
        return cur.matchAllCategories || cur.customerCategories.length > 0 || cur.standardCategories.length > 0;
    }

    isEmpty = () => this.customerCategoriesSelect.isEmpty() &&
        this.standardCategoriesSelect.isEmpty() && !this.matchAllCategories.getValue();

    clear = () => {
        this.matchAllCategories.setValue(DEFAULT_VALUES.matchAllCategories);
        this.setSelectInitialState(this.customerCategoriesSelect, DEFAULT_VALUES.customerCategories);
        this.setSelectInitialState(this.standardCategoriesSelect, DEFAULT_VALUES.standardCategories);
    }

    haveDifferentFlatItems = (arr1, arr2) =>
        arr1.length !== arr2.length ||
        arr1.some((item, index) =>
            item !== arr2[index] &&
            this.toSelectValue(item) !== arr2[index]
        );

    hasChanged = () => {
        const cur = this.getValue();
        const orig = this.originalValue;
        const matchDiff = orig.matchAllCategories !== cur.matchAllCategories;
        const ccDiff = this.haveDifferentFlatItems(orig.customerCategories, cur.customerCategories);
        const scDiff = this.haveDifferentFlatItems(
            orig.standardCategories.map(this.toSelectData).sort(this.sorter),
            cur.standardCategories
        );
        return matchDiff || ccDiff || scDiff;
    }

    selectCustomCategory = action((item) => {
        this.customerCategoriesSelect.setSelectedValues([
            ...this.customerCategoriesSelect.selectedValues,
            this.toSelectValue(item),
        ]);
    });
}
